import React, { useEffect, useState } from 'react';
import ButtonAccount from '../../../components/ButtonAccount';
import { Controller, useForm } from 'react-hook-form';
import Input from '../../../components/Input';
import { useTrackingContext } from '../../../context/trackingContext';
import { useUserContext } from '../../../context/userContext';
import useClient from '../../../hooks/useClient';
import config from '../../../config';
import Alert from '../../../components/Alert';

const PasswordAndSecurity = () => {
  const { trackPageView, trackFormStart, trackFormSubmit, trackClick, trackError } =
    useTrackingContext();

  const { loginHistory } = useUserContext();
  const client = useClient();

  useEffect(() => {
    trackPageView({
      pageName: 'Password and security page'
    });
  }, []);

  const [isEdit, setIsEdit] = useState(false);
  const [alert, setAlert] = useState(false);
  const [ipAlert, setIpAlert] = useState(false);
  const [failureAlert, setFailureAlert] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid, isDirty, isSubmitting }
  } = useForm({ mode: 'onTouched' });

  useEffect(() => {
    isDirty &&
      trackFormStart({
        formName: 'Password edit start'
      });
  }, [isDirty]);

  const renderButtons = () => {
    return isEdit ? (
      <div className="flex">
        <ButtonAccount disabled={!isValid || !isDirty} label="Save" type="submit" />
        <ButtonAccount type="reset" label="Cancel" />
      </div>
    ) : (
      <ButtonAccount
        label="Edit"
        disabled={isSubmitting}
        onClick={() => {
          trackClick({ btnName: 'Edit password open' });
          setIsEdit(true);
        }}
      />
    );
  };

  const onSubmit = async (data) => {
    trackFormSubmit({
      formName: 'edit password submit'
    });

    try {
      const res = await client.put(`${config.everywhereApi}/users/self/profile/update-password`, {
        oldPassword: data.currentPassword,
        newPassword: data.password
      });

      setIpAlert(null);
      setAlert({ text: res.data.data.message, success: true });
    } catch (err) {
      const error = err?.response?.data?.error;

      trackError({ error_text: 'error updating password' });

      setIpAlert(null);
      setAlert({
        text: error ? error?.errorMessage + ' - ' + error.errorCode : 'Unknown error',
        success: false
      });
    }
  };

  return (
    <div id="passwordAndSecurity">
      <div className="mb-10 mt-8">
        <form
          onSubmit={handleSubmit((data) => {
            setIsEdit(false);
            onSubmit(data);
          })}
          onReset={() => {
            reset();
            setIsEdit(false);
          }}>
          <div className="flex justify-between mb-6">
            <h2 className="font-Korolev-Bold text-xl whitespace-nowrap">Update Password</h2>
            <hr className="my-auto w-full align-middle mx-6 border-dotted border-l-0 border-b-0 border-r-0 " />
            <div>{renderButtons()}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" aria-disabled={!isEdit}>
            <div>
              <Controller
                defaultValue=""
                control={control}
                name="currentPassword"
                rules={{ required: true }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <Input
                    disabled={!isEdit}
                    type="password"
                    placeholder="Current Password"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    className="w-full"
                    passwordVisiblity={isEdit}
                    error={error}
                  />
                )}
              />
            </div>
            <div></div>
            <div>
              <Controller
                defaultValue=""
                control={control}
                name="password"
                rules={{
                  required: 'Password is required',
                  minLength: { value: 8, message: 'Password must be at least 8 characters long' },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
                    message:
                      'Password must contain at least one lowercase letter, one uppercase letter, one number, and one of the following special characters: @,$,!,%,*,?, and &. Other special characters are not accepted.'
                  }
                }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <Input
                    disabled={!isEdit}
                    type="password"
                    placeholder="New Password"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    className="w-full"
                    questionIcon={isEdit}
                    passwordVisiblity={isEdit}
                    tooltip={
                      <>
                        <h2 className="font-Korolev-Bold text-base">PASSWORD REQUIREMENTS:</h2>
                        <ul className="list-disc text-[13px] pl-2.5 list-inside marker:text-lg">
                          <li>
                            <span className="-ml-2">1 Letter, 1 Capital Letter, 1 Number</span>
                          </li>
                          <li>
                            <span className="-ml-2">
                              1 Special Character: @, $, !, %, *, ? and & only
                            </span>
                          </li>

                          <li>
                            <span className="-ml-2">No character may repeat more than 2 times</span>
                          </li>

                          <li>
                            <span className="-ml-2">Between 8 and 32 characters in length</span>
                          </li>
                        </ul>
                      </>
                    }
                    error={error}
                  />
                )}
              />
            </div>

            <div>
              <Controller
                disabled={!isEdit}
                control={control}
                name="rePassword"
                defaultValue=""
                rules={{
                  required: true,
                  validate: (value) => value === watch('password') || 'Passwords do not match'
                }}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <Input
                    disabled={!isEdit}
                    type="password"
                    placeholder="Confirm New Password"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    className="w-full"
                    passwordVisiblity={isEdit}
                    error={error}
                  />
                )}
              />
            </div>
          </div>
        </form>

        {alert && (
          <div className="py-5">
            <Alert setShowAlert={setAlert} loggedIn success={alert.success}>
              <p className="pr-12 text-sm md:text-base">{alert.text}</p>
            </Alert>
          </div>
        )}
      </div>

      <div className="mb-10">
        <h2 className="font-Korolev-Bold text-xl mb-2">Login History</h2>
        <hr className="w-full border-dotted border-l-0 border-b-0 border-r-0 mb-5" />

        <div className="overflow-x-auto">
          <table className="table-auto w-full min-w-[600px]">
            <thead>
              <tr className="text-left">
                <th className="pb-3 w-1/4">IP Address</th>
                <th className="pb-3">Date and Time</th>
                <th className="text-right pb-3">Country/Region</th>
              </tr>
            </thead>
            <tbody>
              {loginHistory.map((login, idx) => (
                <tr key={idx}>
                  <td className="pb-3">{login.ipAddress}</td>
                  <td className="pb-3">{login.dateAdded}</td>
                  <td className="pb-3 text-right">{login.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PasswordAndSecurity;
